import React, { useEffect, useState, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "axios";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { IconSearch } from "@tabler/icons";
import { useSelector, useDispatch } from "react-redux";
import { addItemToTableData } from "views/Sales-Sections/salesReturn/slice/tableDataSlice";
// Assuming these are imported from a central config file
import { redirectUrl, baseUrl } from "config";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackBar } from "context/snackbarContext";
import "./addOnBtn.css";

// --- IMPORTANT: Ensure BatchWindow and MultiUnitWindow are passed as props ---
// These components are expected to be passed from the parent component that renders DrawerItems.
// Example usage in parent:
// import BatchWindowPos from "./salesPos/components/BatchWindowPos";
// import MultiUnitWindowPos from "./salesPos/components/MultiUnitWindowPos";
// <DrawerItems
//   BatchWindow={BatchWindowPos}
//   MultiUnitWindow={MultiUnitWindowPos}
//   // ... other props
// />

const colors = [
  "#2E8B57", // SeaGreen
  "#8B0000", // DarkRed
  "#4B0082", // Indigo
  "#556B2F", // DarkOliveGreen
  "#8B008B", // DarkMagenta
  "#FF4500", // OrangeRed
  "#483D8B", // DarkSlateBlue
  "#006400", // DarkGreen
  "#8B4513", // SaddleBrown
  "#4682B4", // SteelBlue
];

function DrawerItems({
  typ, // e.g., "sales" or "salesReturn"
  expiry,
  batchWise, // "YES BATCH" or other values
  addItemToPos, // Redux action for adding item to sales POS table
  selectedRowOrCurrentRow, // Redux action to select a row
  updateBatch, // Redux action to update batch in table
  updateQtyAndTotal, // Redux action to update quantity and total in table
  BatchWindow, // Component for batch selection, passed as a prop
  MultiUnitWindow, // Component for multi-unit selection, passed as a prop
  tableDataPos, // Current state of the sales table data (from Redux)
  netTotal, // Current net total (from Redux)
}) {
  const shadowInside = {
    boxShadow:
      "rgba(50, 50, 93, 0.25) 0px 30px 60px -12px inset, rgba(0, 0, 0, 0.3) 0px 18px 36px -18px inset",
  };
  const shadowOutSide = {
    boxShadow:
      "rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px, rgba(10, 37, 64, 0.35) 0px -2px 6px 0px inset",
  };
  const theme = useTheme();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackBar();

  // Redux Selectors - optimized to prevent unnecessary re-renders
  const allProductsData = useSelector(
    (state) => state.ProductsData?.data?.result || []
  );
  const isAddOnAlways = useSelector((state) => state.common.isADDon);
  const taxUtilitiesListData = useSelector(
    (state) => state.common.taxUtilitiesListData
  );

  // Component State
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isProductOrAddon, setIsProductOrAddon] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryMenuItems, setCategoryMenuItems] = useState([]);
  const [currProduct, setCurrProduct] = useState(null); // Product selected for modal (batch/multi-unit)
  const [modalTyp, setModalTyp] = useState(null); // Type of modal to open ("batchWise" or "multiUnit")
  const [addOnItems, setAddOnItems] = useState([]); // Correctly declared and initialized addOnItems state
  const [itemQuantities, setItemQuantities] = useState({}); // Stores quantities for items displayed in the grid

  const itemsPerPage = 198; // Number of items to display per page
  const regex = /^[A-Z]/; // Regex to check if image path is not a base64 string

  // --- Modal Control (Defined before usage in handleAddItem) ---
  const handleClickOpen = useCallback((type) => {
    setModalTyp(type);
    setOpenModal(true);
  }, []); // No dependencies as it only updates local state

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setCurrProduct(null); // Clear the current product when modal closes
  }, []); // No dependencies as it only updates local state

  // --- Effects for Data Loading ---

  // Initialize allProducts and filteredProducts from Redux data
  useEffect(() => {
    if (allProductsData.length > 0) {
      setAllProducts(allProductsData);
      setFilteredProducts(allProductsData); // Initialize filtered products
    }
  }, [allProductsData]); // Depend on allProductsData to update only when it changes

  // Fetch categories and add-ons on component mount
  // Memoize getCategory to prevent re-running this effect unnecessarily
  const getCategory = useCallback(async (name, setStateFunction) => {
    try {
      const res = await axios({
        method: "post",
        url: baseUrl + "/getCategoryOrSubcategory",
        data: { typ: name },
        withCredentials: true,
      });
      // To show all products, include an "All" option for product groups
      setStateFunction(
        name === "PRODUCT GROUP"
          ? [{ Typ: "All" }, ...res.data]
          : [...res.data]
      );
    } catch (err) {
      console.error(`Error fetching categories/add-ons (${name}):`, err);
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        redirectUrl(); // Redirect to login or home if unauthorized
      } else {
        showSnackbar("error", `Failed to load ${name === "PRODUCT GROUP" ? "categories" : "add-ons"}.`);
      }
    }
  }, [showSnackbar]); // showSnackbar is stable from context

  useEffect(() => {
    getCategory("PRODUCT GROUP", setCategoryMenuItems);
    getCategory("ADD_ON", setAddOnItems);
  }, [getCategory]); // Dependency on memoized getCategory ensures this runs once

  // --- Handlers for Filtering and Search ---

  // Handle category filtering for products displayed in the grid
  const handleCategory = useCallback((value) => {
    setCurrentPage(1); // Reset to first page on category change
    if (value === "All") {
      setFilteredProducts(allProducts);
    } else {
      setFilteredProducts(allProducts.filter((item) => item.Size === value));
    }
  }, [allProducts]); // Depend on allProducts to ensure latest data is used

  // Handle search field input for filtering products
  const handleSearchField = useCallback((value) => {
    setSearchName(value);
    setCurrentPage(1); // Reset to first page on search
    setFilteredProducts(
      allProducts.filter((item) =>
        item.ItemNameTextField.toLowerCase().includes(value.toLowerCase())
      )
    );
  }, [allProducts]); // Depend on allProducts

  // Clear search field and reset product list
  const clearField = useCallback(() => {
    setSearchName("");
    setFilteredProducts(allProducts);
    setCurrentPage(1); // Reset to first page
  }, [allProducts]); // Depend on allProducts

  // --- Pagination Logic ---

  // Memoized calculation for items to render on the current page
  const itemsToRender = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts?.slice(startIndex, endIndex) || [];
  }, [currentPage, filteredProducts, itemsPerPage]);

  const handleNextClick = useCallback(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  }, []);

  const handlePrevClick = useCallback(() => {
    setCurrentPage((prevPage) => Math.max(1, prevPage - 1)); // Ensure page doesn't go below 1
  }, []);

  // --- Core Logic for Adding Items/Add-ons to Sales Table ---

  // Handle adding add-ons to a selected table row
  const handleAddAddOns = useCallback((item) => {
    const existingItemIndex = tableDataPos.findIndex(
      (items) => items.selectedRow === true
    );

    if (existingItemIndex !== -1) {
      const selectedItem = tableDataPos[existingItemIndex];

      // Ensure batch is an array to avoid errors
      const currentBatches = Array.isArray(selectedItem.batch)
        ? selectedItem.batch
        : (selectedItem.batch === "NIL" || !selectedItem.batch ? [] : [selectedItem.batch]);

      if (currentBatches.includes(item.Typ)) {
        showSnackbar(
          "error",
          `Add-on "${item.Typ}" already exists in ${selectedItem.ItemNameTextField}`
        );
      } else {
        dispatch(
          updateBatch({
            index: existingItemIndex,
            batch: [...currentBatches, item.Typ], // Add new add-on to the array
          })
        );
        dispatch(
          selectedRowOrCurrentRow({
            name: selectedItem.ItemNameTextField, // Re-select to trigger UI update if needed
          })
        );
        showSnackbar(
          "success",
          `Add-on "${item.Typ}" added to "${selectedItem.ItemNameTextField}"`
        );
      }
    } else {
      // If no row is selected, provide feedback.
      showSnackbar("error", "Please select an item in the sales table first to add an add-on.");
    }
  }, [tableDataPos, dispatch, updateBatch, selectedRowOrCurrentRow, showSnackbar]);

  // Handle adding product items to the sales table
  const handleAddItem = useCallback((item, quantity) => {
    const TaxEnabled = taxUtilitiesListData?.find(
      (data) => data.Typ === "SALES_B2B_AND_B2C"
    );
    const isTaxEnabled = TaxEnabled?.Nam === "YES";

    const baseQuantity = quantity ?? 1;

    // Check if the item already exists in the table (by ItemC)
    const existingItemIndex = tableDataPos.findIndex(
      (existingTableItem) => existingTableItem.ItemC === item.ItemC
    );

    if (existingItemIndex !== -1) {
      // Item already exists in the table, update its quantity and total
      const existingTableItem = tableDataPos[existingItemIndex];
      const updatedQty = existingTableItem.qty + baseQuantity;

      let updatedTotal = updatedQty * existingTableItem.Rate1;
      let taxAmt = 0;

      if (isTaxEnabled) {
        // If tax is enabled, calculate tax amount considering Rate1 includes tax
        taxAmt = (updatedQty * existingTableItem.Rate1 * existingTableItem.Tax1) / (100 + existingTableItem.Tax1);
      } else {
        // If tax is not enabled or Rate1 is exclusive of tax, calculate tax and add to total
        taxAmt = ((updatedQty * existingTableItem.Rate1) / 100) * existingTableItem.Tax1;
        updatedTotal += taxAmt; // Add tax to total if Rate1 is exclusive
      }

      dispatch(
        updateQtyAndTotal({
          index: existingItemIndex,
          qty: updatedQty,
          total: updatedTotal,
          taxAmt: taxAmt,
        })
      );
      dispatch(selectedRowOrCurrentRow({ name: item.ItemNameTextField })); // Select the updated row
      showSnackbar(
        "success",
        `Updated "${item.ItemNameTextField}" - Qty: ${updatedQty}, Total: ${updatedTotal.toFixed(3)}`
      );
    } else {
      // Item does not exist, add as a new row
      if (item) {
        let newItem = {
          ...item,
          qty: baseQuantity,
          exp_date: dayjs().format("DD/MM/YYYY"), // Set default expiry date
          batch: isAddOnAlways ? [] : "NIL", // Initialize batch as an array if add-ons are enabled
          selectedRow: false, // New items are not initially selected
        };

        let calculatedTotal = newItem.qty * newItem.Rate1;
        let calculatedTaxAmt = 0;

        if (isTaxEnabled) {
          calculatedTaxAmt = (newItem.qty * newItem.Rate1 * newItem.Tax1) / (100 + newItem.Tax1);
        } else {
          calculatedTaxAmt = ((newItem.qty * newItem.Rate1) / 100) * newItem.Tax1;
          calculatedTotal += calculatedTaxAmt; // Add tax if Rate1 is exclusive
        }

        newItem = {
          ...newItem,
          total: calculatedTotal,
          taxAmt: calculatedTaxAmt,
        };

        setCurrProduct(newItem); // Set the current product to be handled by the modal

        // Determine if a modal (multi-unit or batch) needs to be opened
        // handleClickOpen is now defined before this point
        if (newItem.Pack !== 1) {
          handleClickOpen("multiUnit");
        } else if (batchWise === "YES BATCH") {
          handleClickOpen("batchWise");
        } else {
          // If no modal needed, directly dispatch the item to the table
          if (typ === "salesReturn") {
            dispatch(addItemToTableData(newItem));
          } else {
            dispatch(addItemToPos(newItem));
          }
          showSnackbar("success", `Added new item: "${newItem.ItemNameTextField}"`);
        }
      }
    }
  }, [
    dispatch,
    tableDataPos,
    taxUtilitiesListData,
    isAddOnAlways,
    batchWise,
    typ,
    addItemToTableData,
    addItemToPos,
    updateQtyAndTotal,
    selectedRowOrCurrentRow,
    showSnackbar,
    handleClickOpen, // Crucial dependency for useCallback
  ]);

  // --- Quantity Management for Grid Items ---

  // Function to update the quantity for an item in the local state
  const updateItemQuantity = useCallback((itemC, delta) => {
    setItemQuantities((prevQuantities) => {
      const currentQuantity = prevQuantities[itemC] || 1;
      const newQuantity = Math.max(1, currentQuantity + delta); // Ensure quantity doesn't go below 1
      return { ...prevQuantities, [itemC]: newQuantity };
    });
  }, []); // No dependencies as it only updates local state based on previous

  // --- Render Functions for Grid Items ---

  // Memoized function to render individual product cards
  const renderProductCard = useCallback((item) => {
    const quantity = itemQuantities[item.ItemC] || 1; // Get quantity from local state

    let imgSrc = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcThtjU0d_BQklzBkT7Hn7t48a5yaBVWIJa4i6PcFbFgt91JYcN-FPV0laysIBBD-VC-p-s&usqp=CAU";
    if (item.photo && item.photo !== "null" && !regex.test(item.photo)) {
      try {
        const parsedPhoto = JSON.parse(item.photo);
        if (parsedPhoto.img) {
          imgSrc = parsedPhoto.img;
        }
      } catch (e) {
        // console.error("Error parsing JSON for image:", e, "Item photo:", item.photo);
        // Fallback to default image if JSON parsing fails
      }
    }

    return (
      <Grid item xs={6} sm={4} key={item.ItemC}> {/* Use a stable key like ItemC */}
        <Card
          sx={{
            p: 1,
            width: "100%",
            height: "160px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            "&:hover": {
              backgroundColor: theme.palette.action.hover, // Use theme for hover
            },
            cursor: "pointer",
          }}
        >
          <Box
            onClick={() => handleAddItem(item, quantity)}
            sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "center" }}
          >
            <img
              style={{
                width: "100%",
                height: "80px",
                objectFit: "contain",
              }}
              src={imgSrc}
              alt={item.ItemNameTextField}
            />
            <p
              style={{
                width: "-webkit-fill-available",
                border: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                margin: "0rem",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              {item.ItemNameTextField}
            </p>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Price: <strong>{item.Rate1}</strong>
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={"2px"}
            mt={0.5}
          >
            <IconButton
              color="primary"
              aria-label="Decrease quantity"
              sx={{ color: theme.palette.primary.main, p: 0 }} // Use theme color
              onClick={(e) => { e.stopPropagation(); updateItemQuantity(item.ItemC, -1); }}
              disabled={quantity <= 1} // Disable if quantity is 1
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              Qty: <strong>{quantity}</strong>
            </Typography>
            <IconButton
              color="primary"
              aria-label="Increase quantity"
              sx={{ color: theme.palette.primary.main, p: 0 }} // Use theme color
              onClick={(e) => { e.stopPropagation(); updateItemQuantity(item.ItemC, 1); }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Card>
      </Grid>
    );
  }, [handleAddItem, itemQuantities, updateItemQuantity, theme]); // Dependencies for renderProductCard

  // Memoized function to render add-on buttons
  const renderAddOnButton = useCallback((item, index) => {
    const color = colors[index % colors.length]; // Cycle through defined colors
    return (
      <Grid item xs={6} sm={4} key={item.Typ}> {/* Use item.Typ as key for add-ons */}
        <Box
          className="button-30" // Assuming this className applies custom styling
          sx={{ backgroundColor: color }}
          onClick={() => handleAddAddOns(item)}
        >
          {item.Typ}
        </Box>
      </Grid>
    );
  }, [handleAddAddOns]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Modals for Batch and Multi-Unit Selection */}
      <Dialog
        open={openModal}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        maxWidth="sm" // Set a max width for the dialog
        fullWidth // Allow dialog to take full width up to maxWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Conditional rendering for MultiUnitWindow and BatchWindow */}
          {modalTyp === "batchWise" && BatchWindow ? (
            <BatchWindow
              product={currProduct}
              modalClose={handleClose}
              batchWise={batchWise}
              expiry={expiry}
              typ={typ}
            />
          ) : modalTyp === "multiUnit" && MultiUnitWindow ? (
            <MultiUnitWindow
              product={currProduct}
              expiry={expiry}
              modalClose={handleClose}
              typ={typ}
            />
          ) : (
            // Fallback or error message if modal type is unknown or component is missing
            <Typography variant="h6" sx={{ p: 2, color: theme.palette.error.main }}>
              Error: Modal component not found or type not recognized.
              Please ensure `BatchWindow` and `MultiUnitWindow` are passed as props.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Header Section: Category, Search, Net Total */}
      <Box
        sx={{
          flex: "0 0 auto", // Do not grow or shrink, take auto height
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: theme.palette.background.default,
          py: 1,
          px: 2, // Add horizontal padding
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <Autocomplete
              disablePortal
              id="category-autocomplete"
              options={categoryMenuItems}
              getOptionLabel={(option) => option.Typ}
              onChange={(e, v) => {
                if (v) handleCategory(v.Typ);
              }}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <OutlinedInput
              size="small"
              sx={{ width: "100%", pr: 1, pl: 2, my: 1 }} // Adjusted vertical margin
              id="product-search-input"
              value={searchName}
              onChange={(e) => handleSearchField(e.target.value)}
              placeholder="Search products"
              startAdornment={
                <InputAdornment position="start">
                  <IconSearch
                    stroke={1.5}
                    size="1rem"
                    color={theme.palette.grey[500]}
                  />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  {searchName !== "" && (
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={clearField}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              }
              aria-describedby="search-helper-text"
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4">Net Total :</Typography>
              <Typography variant="h4" sx={{ textAlign: "right", fontWeight: 'bold', color: theme.palette.primary.dark }}>
                {netTotal?.toFixed(3) || "0.000"}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ my: 1 }} /> {/* Add margin around divider */}

      {/* Product/Add-on Toggle Buttons */}
      {isAddOnAlways && (
        <Box sx={{ px: 2, pb: 1 }}> {/* Add padding to buttons section */}
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                variant={isProductOrAddon ? "contained" : "outlined"}
                onClick={() => { setIsProductOrAddon(true); setCurrentPage(1); }} // Reset page on toggle
                sx={{
                  background: isProductOrAddon ? theme.palette.primary.main : theme.palette.grey[200],
                  color: isProductOrAddon ? "white" : theme.palette.text.primary,
                  boxShadow: isProductOrAddon ? shadowOutSide : shadowInside,
                  ":hover": {
                    background: isProductOrAddon ? theme.palette.primary.dark : theme.palette.grey[300],
                  },
                }}
                fullWidth
              >
                Products
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant={!isProductOrAddon ? "contained" : "outlined"}
                onClick={() => { setIsProductOrAddon(false); setCurrentPage(1); }} // Reset page on toggle
                sx={{
                  background: !isProductOrAddon ? theme.palette.primary.main : theme.palette.grey[200],
                  color: !isProductOrAddon ? "white" : theme.palette.text.primary,
                  boxShadow: !isProductOrAddon ? shadowOutSide : shadowInside,
                  ":hover": {
                    background: !isProductOrAddon ? theme.palette.primary.dark : theme.palette.grey[300],
                  },
                }}
                fullWidth
              >
                Add On
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Pagination Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, pb: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrevClick}
          disabled={currentPage === 1}
          startIcon={<KeyboardArrowLeftIcon />}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextClick}
          // Corrected disabled check for next page: if current page covers all filtered products
          disabled={
            (currentPage * itemsPerPage >= filteredProducts.length) ||
            filteredProducts.length === 0
          }
          endIcon={<ArrowForwardIosIcon />}
        >
          Next
        </Button>
      </Box>

      {/* Main Content Area: Products or Add-ons Grid */}
      <Box
        sx={{
          flex: "1 1 auto", // Allow this box to grow and shrink to fill available space
          overflowY: "auto", // Enable vertical scrolling
          borderTop: "1px solid lightgrey",
          p: 1, // Add some padding inside the scrollable area
        }}
      >
        <Grid container spacing={1}>
          {/* Conditionally render products or add-ons */}
          {isProductOrAddon
            ? itemsToRender.map(renderProductCard)
            : addOnItems.map(renderAddOnButton)}
          {/* Display message if no items are found */}
          {isProductOrAddon && itemsToRender.length === 0 && filteredProducts.length > 0 && (
            <Grid item xs={12} sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No products found for the current page.
              </Typography>
            </Grid>
          )}
          {isProductOrAddon && filteredProducts.length === 0 && (
            <Grid item xs={12} sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No products match your search or category.
              </Typography>
            </Grid>
          )}
          {!isProductOrAddon && addOnItems.length === 0 && (
            <Grid item xs={12} sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No add-ons available.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

// Memoize the entire component to prevent re-renders if parent props don't change
export default React.memo(DrawerItems);